import React, { useEffect, useState } from 'react'
import { NetworkStatus } from '@apollo/client'
import type { GetPublicProfileQuery } from 'apollo-gql'
import { useMarketContext } from 'contexts/MarketContext'
import prefetchProfilePage from 'graphql/prefetchers/prefetchProfilePage'
import { initializeApollo } from 'lib/apolloClient'
import AppError from 'lib/AppError'
import { setCacheControlHeader } from 'lib/cache'
import { useTranslation } from 'lib/i18n'
import locationTemporaryRewrite from 'lib/locationTemporaryRewrite'
import config from 'lib/nextConfig'
import {
  trackPotentialProfileViewed,
  trackProfileViewed,
} from 'lib/tracking/avo/trackProfileView'
import { isServer } from 'lib/utils'
import { NextPageContext } from 'next'
import { NextSeo, ProductJsonLd } from 'next-seo'

import 'lib/algolia'

import ErrorMessage from 'components/ErrorMessage/ErrorMessage'
import ProfileNotFoundPage from 'components/NotFound/ProfileNotFoundPage'
import PotentialProfilePage from 'components/PotentialProfilePage/PotentialProfilePage'
import ProfilePage from 'components/ProfilePage/ProfilePage'
import PublicLayout from 'components/PublicLayout/PublicLayout'
import useDetectedMarket from 'hooks/useDetectedMarket'
import useEffectOnce from 'hooks/useEffectOnce'
import useIsDefaultLanguage from 'hooks/useIsDefaultLanguage'
import useMarket from 'hooks/useMarket'
import usePath from 'hooks/usePath'
import usePublicPotentialProfile from 'hooks/usePublicPotentialProfile'
import usePublicProfile from 'hooks/usePublicProfile'
import useRouter from 'hooks/useRouter'
import useTrackAlgoliaSearch from 'hooks/useTrackAlgoliaSearch'

import { algoliaEventNames } from '@/constants'

type OpenGraphImageProps = {
  url: string
  width?: number
  height?: number
  alt?: string
}

type OpenGraphProps = {
  url?: string
  title?: string
  description?: string
  images?: OpenGraphImageProps[]
  // eslint-disable-next-line camelcase
  site_name?: string
  type?: string
}

function ProductJson({
  profile,
}: {
  profile: GetPublicProfileQuery['market']['profile']
}) {
  const { t } = useTranslation()
  const router = useRouter()

  if (!profile?.name) return null
  const webbAppUrl = config.WEBAPP_URL

  const { defaultProfileProduct } = profile
  const { reviewCount = 0 } = profile
  const { averageReviewRating = 0 } = profile
  const { name = '' } = profile
  const { description = '' } = profile

  // As string replace all does not work in all versions of major browsers
  // this function will implement that functionality for "any" browser
  const replace = (initial: string, search: string, replacement: string) => {
    return initial.split(search).join(replacement)
  }
  return (
    <>
      <ProductJsonLd
        productName={t(
          'profileProduct.slug.meta.title',
          'Book a video from {{profileName}}',
          {
            profileName:
              typeof name === 'string' ? replace(name, '"', '\\"') : '',
          },
        )}
        sku={profile.slug}
        mpn={profile.slug}
        description={
          typeof description === 'string'
            ? replace(description, '"', '\\"')
            : ''
        }
        images={[profile.profilePicture?.bigUrl]}
        aggregateRating={
          reviewCount > 0 &&
          averageReviewRating >= 4 && {
            ratingValue: String(averageReviewRating),
            reviewCount: String(reviewCount),
          }
        }
        offers={[
          {
            price: String(
              defaultProfileProduct?.price?.valueWithDecimals || '',
            ),
            priceCurrency: defaultProfileProduct?.price?.currency,
            availability: defaultProfileProduct?.acceptingOrders
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock',
            url: `${webbAppUrl}${router.asPath}`,
            seller: {
              name: 'memmo',
            },
          },
        ]}
      />
      {defaultProfileProduct && (
        <meta itemProp="productID" content={defaultProfileProduct?.id} />
      )}
    </>
  )
}

function Seo({
  profile,
}: {
  profile: GetPublicProfileQuery['market']['profile']
}) {
  const { detectedMarket, getMarketById } = useDetectedMarket()
  const currentMarket = useMarket()
  const isDefaultLanguage = useIsDefaultLanguage()
  const { t } = useTranslation()
  const path = usePath()
  const { markets } = useMarketContext()

  if (!profile?.id) return null

  const { name, defaultMarketId, profileMarkets = [] } = profile
  let url
  let market
  let languageId
  const webbAppUrl = config.WEBAPP_URL
  const profileDefaultMarket = getMarketById(defaultMarketId)
  const defaultMarketSettings = profileMarkets.find(
    (marketSettings) => marketSettings.market.id === defaultMarketId,
  )

  const profileMarketsInUse = markets.filter((mrtk) => {
    const isMarketListed =
      profileMarkets.find(
        (marketSettings) => marketSettings.market.id === mrtk.id,
      )?.showDirect === !defaultMarketSettings?.showDirect

    return defaultMarketSettings?.showDirect ? !isMarketListed : isMarketListed
  })

  let languageAlternates = profileMarketsInUse
    .filter((mrkt) => mrkt.showInSitemap)
    .map((mrkt) => {
      return {
        hrefLang: `${mrkt.defaultLanguage.code}-${mrkt.id}`,
        href: `${webbAppUrl}/${mrkt.id}/${mrkt.defaultLanguage.code}${path.nakedPath}`,
      }
    })

  if (profileMarketsInUse.find((mrkt) => mrkt.id === 'global')) {
    const globalEn = {
      hrefLang: 'en',
      href: `${webbAppUrl}/global/en${path.nakedPath}`,
    }

    languageAlternates = [...languageAlternates, globalEn]
  }

  if (detectedMarket?.detectedBy === 'crawler' && profileDefaultMarket) {
    const language = path.isLocalized
      ? detectedMarket.language
      : profileDefaultMarket?.defaultLanguage

    market = profileDefaultMarket?.id
    languageId = language.id
    url = `${webbAppUrl}/${market}/${language.code}${path.nakedPath}`
  }

  const title = t(
    'profile.slug.meta.title2',
    'Order a personalised video from {{profileName}}',
    {
      profileName: name,
      lng: languageId,
    },
  )

  const descriptionStandard = t(
    'profile.slug.meta.description',
    'Get a personalised video message from {{profileName}}. What are you waiting for? Get {{profileName}} to surprise you loved ones',
    {
      profileName: name,
      lng: languageId,
    },
  )

  const description24h = t(
    'profile.slug.meta.description24h',
    'Get a personalised video message from {{profileName}}. 24 hour delivery. What are you waiting for? Get {{profileName}} to surprise you loved ones',
    {
      profileName: name,
      lng: languageId,
    },
  )

  const profileHas24HourDeliveryForDefaultProduct = (
    p: GetPublicProfileQuery['market']['profile'],
  ) => {
    return !!p?.defaultProfileProduct?.productChildren?.find(
      (childProduct) =>
        childProduct.product.slug === 'video-request-express-delivery' &&
        childProduct.product.expirationTime === '1DAY',
    )
  }

  const description = profileHas24HourDeliveryForDefaultProduct(profile)
    ? description24h
    : descriptionStandard

  const meta: { title: string; description?: string } = {
    title,
  }

  if (currentMarket.id === profile.defaultMarketId) {
    meta.description = profile.description
  }

  const openGraph: OpenGraphProps = {
    ...meta,
    ...(profile.profilePicture?.bigUrl && {
      images: [{ url: profile.profilePicture.bigUrl }],
    }),
    ...(profile.profileVideo?.mp4Url && {
      type: 'video.other',
      videos: [
        {
          url: profile?.profileVideo.mp4Url,
          width: '400',
          height: '532',
        },
      ],
    }),
    url,
  }

  return (
    <NextSeo
      title={title}
      description={description}
      openGraph={openGraph}
      canonical={url}
      noindex={!isDefaultLanguage}
      nofollow={!isDefaultLanguage}
      languageAlternates={languageAlternates}
    />
  )
}

function ProfilePageBySlug(): JSX.Element {
  const [tracked, setTracked] = useState([])
  const router = useRouter()
  const { slug } = router.query
  const { trackAlgoliaEvent } = useTrackAlgoliaSearch()

  const {
    profile,
    error: profileError,
    loading: profileLoading,
    networkStatus: profileNetworkStatus,
  } = usePublicProfile(slug as string)

  const showProfilePage = isServer()
    ? profileNetworkStatus === NetworkStatus.ready
    : profileLoading

  const {
    potentialProfile,
    error: potentialProfileError,
    loading: potentialProfileLoading,
    networkStatus: potentialProfileNetworkStatus,
  } = usePublicPotentialProfile(slug, {
    skip: profile?.slug || showProfilePage,
  })

  const defaultProfileProduct = profile?.defaultProfileProduct

  const showPotentialProfilePage = isServer()
    ? potentialProfileNetworkStatus === NetworkStatus.ready
    : potentialProfileLoading

  useEffect(() => {
    if (!tracked.includes(profile?.slug)) {
      if (profile?.slug && defaultProfileProduct?.price) {
        setTracked([...tracked, profile?.slug])
        trackProfileViewed({
          profile,
          profileProduct: defaultProfileProduct,
          productSlug: defaultProfileProduct.product.slug,
        })
      } else if (potentialProfile?.id) {
        setTracked([...tracked, profile?.slug])
        trackPotentialProfileViewed({
          potentialProfile,
        })
      }
    }
  }, [profile, defaultProfileProduct, potentialProfile, tracked])

  useEffectOnce(() => {
    if (profile?.id) {
      trackAlgoliaEvent('viewedObjectIDs', {
        eventName: algoliaEventNames.PRODUCT_VIEWED,
        objectIds: [profile.id],
        profileId: profile.id,
      })
    }
  })

  if (profileError || potentialProfileError)
    return <ErrorMessage message="Error loading profile." />

  if (showProfilePage || (profile && profile.slug)) {
    const profilePageProps = {
      profile,
      profileSlug: String(slug),
      loading: profileLoading,
    }

    return (
      <PublicLayout nakedHeader hidePress navigationBarDarkContent>
        <Seo profile={profile} />
        <ProductJson profile={profile} />
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* 
        // @ts-ignore */}
        <ProfilePage {...profilePageProps} />
      </PublicLayout>
    )
  }
  if (
    showPotentialProfilePage ||
    (potentialProfile && potentialProfile?.slug)
  ) {
    return (
      <PublicLayout nakedHeader hidePress>
        <Seo profile={potentialProfile} />
        <PotentialProfilePage
          profile={potentialProfile}
          loading={potentialProfileLoading}
        />
      </PublicLayout>
    )
  }
  return <ProfileNotFoundPage />
}

ProfilePageBySlug.getInitialProps = async (ctx: NextPageContext) => {
  const { query, req, res } = ctx
  const { slug = '' } = query

  if (isServer()) {
    setCacheControlHeader({ ctx, ttl: 900 })
    const apolloClient = initializeApollo(ctx)
    const profile = await prefetchProfilePage(apolloClient, { slug })
    const profileSlug = profile?.data?.market?.profile?.slug
    // An old slug of the profile accessed, redirecting
    if (profileSlug && profileSlug !== slug) {
      locationTemporaryRewrite(res, req.url.replace(String(slug), profileSlug))
    }

    if (!profile) {
      throw new AppError(404)
    }

    return {
      initialApolloState: apolloClient.cache.extract(),
    }
  }

  return {}
}

export default ProfilePageBySlug
